import RecipePrintPage from '../../../src/views/RecipePrintPage'
import {
  decodeRecipeId,
  encodeRecipeSlug,
  formatRecipe,
  formatRecipeSlugRedirect,
  openLog,
  serializeObject,
  serverSideRequest,
} from '../../../src/helpers'

// We revalidate the page:
// - every 10 secondes in development environment
// - every hour in production environment
const revalidate =
  process.env.NEXT_PUBLIC_API_ENV === 'development' ? 10 : 60 * 60

export async function getStaticPaths() {
  return { paths: [], fallback: 'blocking' }
}

export async function getStaticProps({ params, locale }) {
  const decodedRecipeId = decodeRecipeId(params.recipeId)
  const availabilityZoneId = process.env.NEXT_PUBLIC_AVAILABILITY_ZONE
  const { data: recipe } = await serverSideRequest({
    name: 'getRecipe',
    recipeId: decodedRecipeId,
    availabilityZoneId,
    language: locale,
    locale,
  })

  if (!recipe) {
    // 404 when missing recipe
    return {
      notFound: true,
      revalidate,
    }
  }

  if (
    recipe?.availabilityZones?.length > 0 &&
    !recipe.availabilityZones.includes(availabilityZoneId)
  ) {
    // 404 when unavailable for AZ
    return {
      notFound: true,
      revalidate,
    }
  }

  if (recipe._id && locale && !recipe?.slugs?.[locale]) {
    openLog('PAGE_NOT_FOUND', {
      type: 'NO_SLUG_LOCALE',
      page: 'RECIPE_PRINT',
      slug: `/${locale}/recipes/${params.recipeId}`,
      slugs: recipe.slugs,
    })
    // 404 when missing localized slug
    return {
      notFound: true,
      revalidate,
    }
  }

  if (recipe && !recipe.isVisible) {
    // 410 when disabled
    return {
      redirect: {
        destination: `/${locale}/recipes/${params.recipeId}/gone`,
        permanent: false,
      },
    }
  }

  if (recipe._id && locale && recipe?.slugs?.[locale]) {
    const localizedSlug = recipe.slugs[locale]
    const encodedSlug = encodeRecipeSlug({
      id: recipe._id,
      slug: localizedSlug,
    })
    const redirect = formatRecipeSlugRedirect(params.recipeId, {
      id: recipe._id,
      slug: localizedSlug,
    })

    if (redirect) {
      const destination = `/${locale}/recipes/${redirect}/print`
      openLog('PAGE_REDIRECT', {
        type: 'WRONG_SLUG_ID',
        page: 'RECIPE_PRINT',
        slug: `/${locale}/recipes/${params.recipeId}/print`,
        redirect: destination,
      })
      // 308 to correct slug
      return {
        redirect: {
          destination,
          permanent: true,
        },
      }
    }

    if (params.recipeId !== encodedSlug) {
      const destination = `/${locale}/recipes/${encodeRecipeSlug({
        id: recipe._id,
        slug: localizedSlug,
      })}/print`
      openLog('PAGE_REDIRECT', {
        type: 'WRONG_SLUG_LOCALE',
        page: 'RECIPE_PRINT',
        slug: `/${locale}/recipes/${params.recipeId}/print`,
        redirect: destination,
      })
      // 308 to correct localized slug
      return {
        redirect: {
          destination,
          permanent: true,
        },
      }
    }
  }

  // 200
  return {
    props: serializeObject({
      slugs: recipe?.slugs,
      recipe: formatRecipe({ ...recipe, source: 'jow' }, 'full'),
    }),
    revalidate,
  }
}

const RecipePrint = ({ recipe, slugs = {} }) => {
  return <RecipePrintPage recipe={recipe} slugs={slugs} />
}

RecipePrint.getAppLayoutProps = () => ({
  disableFooter: true,
  disableHeader: true,
  pageId: 'RECIPE',
})

export default RecipePrint
